<template>
  <v-stepper
    v-model="stepValue"
    alt-labels
  >
    <v-stepper-header role="tablist">
      <template
        v-for="(s, i) in steps"
      >
        <v-stepper-step
          :key="`step-${i}`"
          :step="i + 1"
          :ref="`step-${i}`"
          :color="determineColor(s)"
          complete-icon="mdi-check"
          :complete="isComplete(s)"
          error-icon="mdi-exclamation"
          :rules="[() => s.status !== 'invalid' || 'error']"
          :class="{'clickable': !isDisabled(s), 'disabled': isDisabled(s)}"
          @click="handleStepClicked(i)"
          :tabindex="isDisabled(s) ? '-1' : '0'"
          :aria-label="`Step ${i+1} of ${steps.length}, ${s.status || ''}, ${s.name}`"
          :aria-selected="stepValue == i+1"
          role="tab"
        >
          <span :class="{'font-weight-bold': stepValue == (i + 1)}">{{ s.name }}</span>
        </v-stepper-step>
        <v-divider
          v-if="i !== steps.length - 1"
          :key="`step-divider-${i}`"
          class="h-stepper--divider"
        />
      </template>
    </v-stepper-header>
  </v-stepper>
</template>

<script>

export default {
  name: "AdsHStepper",
  props: {
    value: {
      type: [String, Number],
      default: null
    },
    steps: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      stepValue: this.value || 1
    };
  },
  watch: {
    value(newVal) {
      this.stepValue = newVal;
    }
  }, 
  methods: {
    handleStepClicked(stepIdx) {
      if (!this.isDisabled(this.steps[stepIdx])) {
        this.$emit('changeStep', stepIdx + 1, this.stepValue);
      }
    },
    handleStepKeyDown(e, stepIdx) {
      // add keyboard selection support in the year picker
      if (e.key === "Enter" || e.key === " ") {
        this.handleStepClicked(stepIdx);
      }
    },
    isComplete(step) {
      return step.status && step.status.toLowerCase() === 'complete'
    },
    isDisabled(step) {
      return step.status && step.status.toLowerCase() === 'disabled'
    },
    determineColor(step) {
      if (this.isComplete(step)) {
        return '#00A908'; // success green
      }

      // return undefined to use default
    }
  },
  mounted() {
    // vuetify doesn't have a keyboard event for the header. We add our own to make it accessible.
    for (let i = 0; i < this.steps.length; i++) {
      const ref = this.$refs[`step-${i}`];
      if (ref) {
        ref[0].$el.addEventListener('keydown', (e) => this.handleStepKeyDown(e, i));
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../styles/variables.scss';
  
  // neutral state
  ::v-deep.v-stepper .v-stepper__step:not(.v-stepper__step--active):not(.v-stepper__step--complete):not(.v-stepper__step--error):not(.disabled) .v-stepper__step__step {
    background-color: rgba(255,255,255,0) !important;
    color: $ads-navy;
    border: 2px solid $ads-navy;
  }

  ::v-deep.v-stepper .v-stepper__step {
    &.clickable {
      cursor: pointer;
    }
    &.disabled {
      cursor: not-allowed;
      .v-stepper__step__step {
        background-color: rgba(0, 0, 0, 0.2) !important;
        color: $ads-dark;
      }
      .v-stepper__label {
        color: rgba(0, 0, 0, 0.5);
      }
    }
    .v-stepper__step__step {
      width: 40px;
      height: 40px;
    }
    .v-stepper__label {
      text-align: center;
      color: $ads-dark;
      font-size: 0.875rem;
    }
  }


  // error icon styling
  ::v-deep.v-stepper .v-stepper__step.v-stepper__step--error .v-stepper__step__step {
    border: 2px solid $ads-error-red;
    color: $ads-error-red;
    i {
      font-size: 1.8rem;
    }
  }

  .h-stepper--divider {
    margin-top: 44px !important;
    margin-left: -55px !important;
    margin-right: -55px !important;
  }

  ::v-deep.theme--dark.v-stepper {
    // neutral state
    .v-stepper__step:not(.v-stepper__step--active):not(.v-stepper__step--complete):not(.v-stepper__step--error):not(.disabled) .v-stepper__step__step {
      color: $ads-navy-dark;
      border: 2px solid $ads-navy-dark;
    }
    .v-stepper__step {
      .v-stepper__step__step {
        color: $ads-dark;
      }
      &.disabled {
        cursor: not-allowed;
        .v-stepper__step__step {
          background-color: rgba(0, 0, 0, 0.2) !important;
          color: $ads-white;
        }
      }
      .v-stepper__label {
        color: $ads-white;
      }
    }
    .v-stepper__step.v-stepper__step--error .v-stepper__step__step {
      border: 2px solid $ads-error-red-dark;
      color: $ads-error-red-dark;
    }
    .h-stepper--divider {
      border-color: $ads-white;
    }
  }
</style>
